/**
 * Wie mag wat zien.
 *
 * Twee lagen, en ze zijn allebei nodig. Deze module is de eerste: hij verbergt
 * wat een gebruiker niet mag. De tweede zit in de backend, die het weigert met
 * een 403 — verbergen alleen is geen beveiliging, want een adres is zo getypt.
 *
 * De bron is `/api/admin/auth/me`: de rol plus `allowed_pages`, een lijst met
 * paginanamen die een chatter expliciet gekregen heeft. Owners en managers
 * hebben geen lijst (`null`) en zien alles.
 *
 * `allowed_pages` bevat naast paginanamen ook tokens voor onderdelen
 * (`mass_dm_filter_*`, `chatter_section_*`, `panel_deny_*`). `magRoute` gaat
 * alleen over de zijbalk; de functies onderaan (30-09-2026) over knoppen en
 * vinkjes binnen een scherm.
 */
import { useApi } from './useApi'
import { magMassaPagina } from './massaRechten'

export interface Mij {
  id: number
  username: string
  /** Zoals de gebruiker zichzelf noemt; valt in het scherm terug op username. */
  display_name?: string | null
  role: string
  allowed_pages: string[] | null
  linked_chatter_id: number | null
  /** 07-10-2026: IANA-tijdzone voor plannen en tonen (bv. "Asia/Manila"); null = Amsterdam. */
  timezone?: string | null
}

/** De routes die alleen beheer mag zien. Gemeten met een chatter-token: de
 *  backend geeft hier 403, dus tonen zou een dode knop opleveren. */
const ALLEEN_BEHEER = new Set([
  'posting', 'paid-posting', 'console', 'settings', 'analytics',
  'hashtags', 'rules', 'content', 'mass-messages', 'dashboard',
  'f2f-login',
  // 14-10-2026 [mm]: tracking-pagina, alleen owner/admin (server: require_owner_or_admin).
  'tracking',
  // 20-10-2026 [mm]: Promo Auto MM (server: owner/admin).
  'promo-mm',
  // 21-10-2026 [chat]: Model texts (server: owner/admin).
  'model-texts',
])
// 30-09-2026 (Jordy): `follower-tracking` en `chatters` stonden hierboven. Die
// lijst gaat vóór de rechten, dus een chatter met "Allow Follower Tracking
// page" of "Allow team performance dashboard" kreeg het menu-item nooit. Ze
// hangen nu aan hun recht; zie magRoute en de server (src/api/chatter_rechten.py).

/** Welke `allowed_pages`-naam bij welke route hoort. Ze heten niet overal
 *  hetzelfde: de route is `personas`, het recht heet ook `personas`, maar
 *  `conversations` dekt zowel de inbox als het chatvenster. */
const RECHT_VAN_ROUTE: Record<string, string> = {
  conversations: 'conversations',
  personas: 'personas',
  fans: 'fans',
  earnings: 'earnings',
  'follower-tracking': 'followers',
  chatters: 'chatters',
}

export function useMij() {
  return useApi<Mij>('/api/admin/auth/me')
}

export function isBeheer(mij: Mij | null | undefined): boolean {
  const rol = String(mij?.role ?? '').toLowerCase()
  return rol === 'owner' || rol === 'admin' || rol === 'manager'
}

/** De rol manager (05-10-2026, Jordy): telt voor knoppen nog als beheer, maar
 *  ziet alleen de pagina's hieronder en geen omzet. Zie ook src/api/deps.py. */
export function isManager(mij: Mij | null | undefined): boolean {
  return String(mij?.role ?? '').toLowerCase() === 'manager'
}

/** Owner of admin: alles, inclusief Earnings, Settings en de bedragen. */
export function isVolledigBeheer(mij: Mij | null | undefined): boolean {
  return isBeheer(mij) && !isManager(mij)
}

/** De acht pagina's van een manager; Earnings is voor hem My Earnings. */
const MANAGER_ROUTES = new Set([
  'conversations', 'mass-messages', 'chatters', 'follower-tracking',
  'fans', 'personas', 'earnings',
])

/** De eerste route uit het menu die deze gebruiker mag, voor een doorverwijzing. */
export function eersteToegestaneRoute(mij: Mij | null | undefined, routes: string[]): string | null {
  return routes.find((r) => magRoute(mij, r)) ?? null
}

/**
 * Mag deze gebruiker deze route zien?
 *
 * Zolang `/me` nog niet binnen is, geeft dit `false` voor alles wat niet van
 * iedereen is. Liever een halve seconde een lege zijbalk dan een menu dat
 * openflitst met dingen die je niet mag.
 */
export function magRoute(mij: Mij | null | undefined, route: string): boolean {
  if (!mij) return false
  // 05-10-2026 (Jordy, afgesproken met [chat]): Mass Messages alleen voor
  // owner/admin en een manager met de schuif "Mass Messages page"; sinds
  // 07-10-2026 ook een chatter met die schuif. Zie lib/massaRechten.ts.
  if (route === 'mass-messages') return magMassaPagina(mij)
  // 18-10-2026 (Jordy): de vraaglog van de Help-assistent, alleen de owner
  // (server: require_owner in src/api/routes/admin_help.py).
  if (route === 'help-log') return String(mij.role ?? '').toLowerCase() === 'owner'
  // 23-10-2026 (Jordy, Content fase 2): owner/admin altijd; een manager of
  // chatter alleen met het token content_page (server: src/api/content_rechten.py).
  if (route === 'content') {
    const rol = String(mij.role ?? '').toLowerCase()
    return isVolledigBeheer(mij)
      || ((rol === 'manager' || rol === 'chatter') && (mij.allowed_pages ?? []).includes('content_page'))
  }
  // 26-10-2026 (Jordy): de Social-pagina: owner/admin altijd; een manager per persoon met
  // het token instagram_page; een chatter nooit (server: src/api/routes/admin_social.py).
  if (route === 'instagram') {
    const rol = String(mij.role ?? '').toLowerCase()
    return isVolledigBeheer(mij)
      || (rol === 'manager' && (mij.allowed_pages ?? []).includes('instagram_page'))
  }
  if (isManager(mij)) return MANAGER_ROUTES.has(route)
  if (isBeheer(mij)) return true
  if (ALLEEN_BEHEER.has(route)) return false
  const pages = mij.allowed_pages ?? []
  // "Hide earnings" wint van het recht op Earnings.
  if (route === 'earnings' && pages.includes('hide_earnings')) return false
  // Chatters: team performance of het aanmaakrecht (elk opent zijn tabblad).
  if (route === 'chatters') return magTeamPerformance(mij) || magAccountsAanmaken(mij)
  const nodig = RECHT_VAN_ROUTE[route]
  if (!nodig) return false
  return pages.includes(nodig)
}

/** "Allow team performance dashboard" (token `chatters`); beheer altijd. */
export function magTeamPerformance(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  return isBeheer(mij) || (mij.allowed_pages ?? []).includes('chatters')
}

/** "Allow creating regular chatter accounts" (token `create_chatter_accounts`). */
export function magAccountsAanmaken(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  return isBeheer(mij) || (mij.allowed_pages ?? []).includes('create_chatter_accounts')
}

// ── Knoppen en vinkjes (30-09-2026, Jordy) ──────────────────────────
//
// Het nieuwe dashboard las deze rechten niet: de Mass DM-knop, beide
// methodes en de vinkjes Timewaster / VIP / Private to me stonden er voor
// iedereen. De server weigerde het al met een 403, maar een chatter zag een
// knop die niets deed. Deze functies volgen precies de serverregels — bij een
// wijziging daar hoort er ook een hier:
//
//   Mass DM        admin_conversations.py `_can_mass_dm`
//   Timewaster     admin_conversations.py `_can_mark_timewaster`
//   VIP            admin_conversations.py `_can_mark_vip`
//                  + panel_permissions.py `vip_mark_allowed_by_pages`
//   Private to me  admin_conversations.py `claim_conversation`
//   Assign         admin_conversations.py `_can_manage_all_private_chats`

/** Alleen chatters en viewers worden beperkt; beheer mag alles. */
function beperkt(mij: Mij): boolean {
  const rol = String(mij.role ?? '').toLowerCase()
  return rol === 'chatter' || rol === 'viewer'
}

function heeft(mij: Mij, token: string): boolean {
  return (mij.allowed_pages ?? []).includes(token)
}

/** 04-10-2026 (Jordy): "Dashboard Mass Message" en de doelgroep "Test accounts
 *  only" tijdelijk alleen voor owner en admin (server: `_is_mass_admin`). */
export function isMassaAdmin(mij: Mij | null | undefined): boolean {
  const rol = String(mij?.role ?? '').toLowerCase()
  return rol === 'owner' || rol === 'admin'
}

/** Mag Mass DM, met deze methode of (zonder methode) met één van beide? */
export function magMassDm(mij: Mij | null | undefined, methode?: 'direct' | 'native'): boolean {
  if (!mij) return false
  if (methode === 'direct' && !isMassaAdmin(mij)) return false
  if (!methode) return magMassDm(mij, 'direct') || magMassDm(mij, 'native')
  if (!beperkt(mij)) return true
  if (heeft(mij, 'panel_deny_mass_dm')) return false
  if (methode === 'native') return heeft(mij, 'mass_dm_native')
  return heeft(mij, 'mass_dm')
}

/** Viewers zijn alleen-lezen; chatters mogen tenzij het expliciet uit staat. */
function magMarkeren(mij: Mij): boolean {
  return String(mij.role ?? '').toLowerCase() !== 'viewer'
}

export function magTimewaster(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  if (!beperkt(mij)) return true
  // 20-10-2026 (Jordy): elke chatter mag altijd markeren; geen weigerrecht meer.
  return magMarkeren(mij)
}

/** VIP is een eigen recht. Zonder expliciete keuze volgt het Timewaster,
 *  zoals het vóór 30-09 werkte. */
export function magVip(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  if (!beperkt(mij)) return true
  if (!magMarkeren(mij)) return false
  if (heeft(mij, 'deny_vip_mark')) return false
  if (heeft(mij, 'vip_mark')) return true
  return !heeft(mij, 'deny_timewaster_mark')
}

/** Mag privé-chats van iedereen beheren (toewijzen en vrijgeven). */
export function magPriveBeheer(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  if (!beperkt(mij)) return true
  if (heeft(mij, 'panel_deny_private')) return false
  return String(mij.role ?? '').toLowerCase() === 'chatter' && heeft(mij, 'private_manage_all')
}

/** Mag een chat zelf op "Private to me" zetten. (Vrijgeven van je eigen
 *  privé-chat mag altijd; dat bepaalt het scherm zelf.) */
export function magZelfPrive(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  if (!beperkt(mij)) return true
  if (heeft(mij, 'panel_deny_private')) return false
  return magPriveBeheer(mij) || heeft(mij, 'private_self_claim')
}

/** De historie van Mass Messages (tab Overview). Serverregel:
 *  admin_conversations.py `_can_mass_message_history`. 03-10-2026. */
export function magMassaOverzicht(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  if (!beperkt(mij)) return true
  return heeft(mij, 'chatter_sections_configured')
    && heeft(mij, 'chatter_section_mass_message_tracker')
}

/** Mag een prijs aan een Mass DM hangen (PPV)? Serverregel:
 *  panel_permissions.py `can_send_paid_mass_dm` (weigerteken mass_dm_no_paid). */
export function magBetaaldeMassDm(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  if (!beperkt(mij)) return true
  return !heeft(mij, 'mass_dm_no_paid')
}

/**
 * Mag dit account dit chatpaneel zien? Serverregel: panel_permissions.py
 * `panel_allowed` (weigerteken `panel_deny_<paneel>`, alleen voor chatters en
 * viewers). 02-10-2026 (Jordy): de Easy-stand van Chats verbergt hiermee de
 * filters Spenders en Timewasters voor wie dat paneel niet mag.
 */
export function magPaneel(
  mij: Mij | null | undefined,
  paneel: 'spenders' | 'timewasters' | 'unanswered' | 'private' | 'new_followers',
): boolean {
  if (!mij) return false
  if (!beperkt(mij)) return true
  return !heeft(mij, `panel_deny_${paneel}`)
}

/** 03-10-2026 (Jordy): AI aan/uit per gesprek — beheer altijd, een chatter
 *  alleen met het recht `toggle_ai` (zelfde regel als de server,
 *  `_can_toggle_conversation_ai`). */
export function magAiSchakelen(mij: Mij | null | undefined): boolean {
  if (!mij) return false
  return isBeheer(mij) || (mij.allowed_pages ?? []).includes('toggle_ai')
}

/** 03-10-2026 (Jordy): een chat blokkeren is een recht van de rol (manager,
 *  admin, owner), niet los aan een chatter toe te kennen. Zelfde regel als de
 *  server (`_can_block_conversations`). */
export function magBlokkeren(mij: Mij | null | undefined): boolean {
  return isBeheer(mij)
}
